import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  GraduationCap, Brain, Briefcase, ScrollText, BookOpen, Award,
  Lightbulb, Users, Mail, ArrowRight, Building2, Globe, Telescope
} from "lucide-react";
import ExecutiveHero from "@/components/ExecutiveHero";
import { FOUNDER_PORTRAIT } from "@/lib/brand";

const EDUCATION = [
  { degree: "PhD · Leadership Studies", institution: "Our Lady of the Lake University",
    note: "Doctoral research on workforce intelligence, institutional leadership, and credentialing reform." },
];

const EXPERIENCE = [
  { role: "Founder & President", org: "Zodiac Workforce Intelligence Institute",
    desc: "Architecting the institutional doctrine and ecosystem for workforce intelligence in the Human-AI era." },
  { role: "Editor-in-Chief", org: "Society & Credential Fatigue Review (SCFR)",
    desc: "Leading peer scholarship on workforce alignment, credential fatigue, and institutional reform." },
];

const RESEARCH = [
  "Workforce Intelligence", "WAIT Theory", "Human-AI Workforce Integration",
  "Credential Fatigue", "Workforce Development", "Leadership and Service Quality",
  "Organizational Performance", "Institutional Performance",
];

const SPEAKING = [
  "Workforce Intelligence Science",
  "WAIT Theory & Institutional Doctrine",
  "AI-Era Human Capital Strategy",
  "Credentialing Reform & Workforce Alignment",
  "Executive Leadership in Hybrid Intelligence Systems",
  "Institutional Modernization for Public & Private Sectors",
];

export default function Founder() {
  return (
    <div className="min-h-screen zodiac-page" data-testid="founder-page">
      <ExecutiveHero
        eyebrow="Founder & President"
        title={<>Dr. Spencer W.</>}
        accentTitle="Charles, PhD."
        subtitle="Founder and President of the Zodiac Workforce Intelligence Institute and Editor-in-Chief of the Society & Credential Fatigue Review. Architect of WAIT Theory — the constitutional framework for workforce alignment in the Human-AI era."
        image={FOUNDER_PORTRAIT}
        breadcrumb={[{ label: "Home", href: "/" }, { label: "About", href: "/about" }, { label: "Founder" }]}
        actions={[
          { label: "Research & Publications", href: "/research", testId: "founder-research-cta" },
          { label: "Contact", href: "/contact", variant: "ghost" },
        ]}
      />

      {/* Executive Biography */}
      <section className="bg-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="founder-biography">
          <div className="zodiac-eyebrow mb-3 flex items-center gap-3">
            <span className="w-8 h-px bg-[#1E3A8A]" />
            Executive Biography
          </div>
          <div className="prose prose-lg max-w-none text-[#475569] leading-relaxed">
            <p>
              Dr. Spencer W. Charles is the Founder and President of the Zodiac Workforce
              Intelligence Institute (ZWII), a premium institutional ecosystem dedicated to the
              scientific advancement of workforce intelligence in the Human-AI era. He serves
              concurrently as Editor-in-Chief of the Society & Credential Fatigue Review (SCFR),
              the Institute's flagship peer-reviewed journal.
            </p>
            <p>
              His scholarly work introduces and refines <strong className="text-[#0F1E47]">
              WAIT Theory — Workforce Alignment Intelligence Theory</strong> — a constitutional
              doctrine positioning workforce intelligence as a measurable institutional science.
              Through WAIT Theory, Dr. Charles defines the Five-Layer Architecture, the Workforce
              Intelligence Index (WII), and the Seven Forces of Workforce Alignment that together
              form the diagnostic and developmental infrastructure of the Institute.
            </p>
            <p>
              Dr. Charles operates at the convergence of workforce development, leadership
              science, quality management, and artificial intelligence integration. His mission
              is to transform institutional capacity from credential-based assumptions to
              intelligence-based measurement.
            </p>
          </div>
        </div>
      </section>

      {/* Education */}
      <Section title="Education" accent="#991B1B" testId="founder-education">
        <div className="space-y-3">
          {EDUCATION.map((e, i) => (
            <div key={i} className="zodiac-hairline-card p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-[#991B1B]/10 flex items-center justify-center flex-shrink-0">
                  <GraduationCap className="w-5 h-5 text-[#991B1B]" />
                </div>
                <div>
                  <h3 className="zodiac-heading text-lg font-bold text-[#0F1E47]">{e.degree}</h3>
                  <p className="text-[#475569] text-sm mt-1">{e.institution}</p>
                  {e.note && <p className="text-xs text-[#6B7280] mt-2">{e.note}</p>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Professional Experience */}
      <Section title="Professional Experience" accent="#1E3A8A" warm testId="founder-experience">
        <div className="space-y-3">
          {EXPERIENCE.map((e, i) => (
            <div key={i} className="zodiac-hairline-card p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-[#1E3A8A]/10 flex items-center justify-center flex-shrink-0">
                  <Briefcase className="w-5 h-5 text-[#1E3A8A]" />
                </div>
                <div>
                  <h3 className="zodiac-heading text-lg font-bold text-[#0F1E47]">{e.role}</h3>
                  <p className="text-[#475569] text-sm">{e.org}</p>
                  <p className="text-sm text-[#6B7280] mt-2">{e.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Research Interests */}
      <Section title="Research Interests" accent="#C8A157" testId="founder-research-interests">
        <div className="grid sm:grid-cols-2 gap-3">
          {RESEARCH.map((r) => (
            <div key={r} className="flex items-center gap-3 p-4 rounded-lg border border-[#E5E7EB] bg-white">
              <span className="w-2 h-2 rounded-full bg-[#C8A157] flex-shrink-0" />
              <span className="text-[#0F1E47] font-medium text-sm">{r}</span>
            </div>
          ))}
        </div>
      </Section>

      {/* WAIT Theory Development */}
      <Section title="WAIT Theory Development" accent="#0F1E47" warm testId="founder-wait">
        <div className="zodiac-hairline-card p-8">
          <div className="flex items-center gap-3 mb-4">
            <Brain className="w-6 h-6 text-[#0F1E47]" />
            <h3 className="zodiac-heading text-xl font-bold text-[#0F1E47]">Workforce Alignment Intelligence Theory</h3>
          </div>
          <p className="text-[#475569] leading-relaxed mb-5">
            Dr. Charles is the architect of WAIT Theory, the foundational doctrine of Workforce
            Intelligence Science. The theory frames workforce dysfunction not as a skills problem
            but as a structural alignment intelligence failure — measurable, developable, and
            institutionally transformable.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/wait-theory">
              <button className="h-11 px-5 rounded-md bg-[#0F1E47] text-white hover:bg-[#1E3A8A] font-semibold inline-flex items-center gap-2">
                Explore WAIT Theory <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
            <Link to="/scfr/wait-theory-2-constitutional-authority-edition-2026">
              <button className="h-11 px-5 rounded-md border border-[#0F1E47]/15 text-[#0F1E47] hover:bg-white inline-flex items-center gap-2">
                <BookOpen className="w-4 h-4" /> Constitutional Authority Edition
              </button>
            </Link>
          </div>
        </div>
      </Section>

      {/* Publications */}
      <Section title="Publications & Research Contributions" accent="#991B1B" testId="founder-publications">
        <div className="zodiac-hairline-card p-8">
          <div className="flex items-start gap-4 mb-5">
            <ScrollText className="w-6 h-6 text-[#991B1B] flex-shrink-0 mt-1" />
            <div>
              <h3 className="zodiac-heading text-xl font-bold text-[#0F1E47] mb-2">
                Featured Publication
              </h3>
              <p className="text-[#0F1E47] font-medium">
                WAIT Theory 2.0 — Workforce Alignment Intelligence Theory for the Human-AI Era
              </p>
              <p className="text-sm text-[#6B7280] mt-1">SCFR Constitutional Authority Edition · 2026</p>
            </div>
          </div>
          <p className="text-[#475569] text-sm leading-relaxed mb-5">
            Dr. Charles is the principal author of the constitutional doctrine and the
            originating researcher behind the Workforce Intelligence Index, the Five-Layer
            Architecture, and the Seven Forces convergence model.
          </p>
          <Link to="/research">
            <button className="h-11 px-5 rounded-md bg-[#991B1B] text-white hover:bg-[#7F1D1D] font-semibold inline-flex items-center gap-2">
              Full Research Program <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
        </div>
      </Section>

      {/* Institutional Leadership */}
      <Section title="Institutional Leadership" accent="#0F766E" warm testId="founder-leadership">
        <div className="grid md:grid-cols-3 gap-3">
          {[
            { icon: Building2, label: "ZWII Presidency",     desc: "Founding executive leadership of the Institute" },
            { icon: BookOpen,  label: "SCFR Editorship",     desc: "Editorial direction of the peer-reviewed journal" },
            { icon: Telescope, label: "Observatory Direction", desc: "Strategic oversight of workforce intelligence instrumentation" },
          ].map((l) => (
            <div key={l.label} className="zodiac-hairline-card p-5">
              <l.icon className="w-5 h-5 text-[#0F766E] mb-3" />
              <h4 className="zodiac-heading text-base font-bold text-[#0F1E47] mb-1">{l.label}</h4>
              <p className="text-sm text-[#6B7280]">{l.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Speaking & Consulting */}
      <Section title="Speaking & Consulting Areas" accent="#1E3A8A" testId="founder-speaking">
        <div className="grid sm:grid-cols-2 gap-3">
          {SPEAKING.map((s) => (
            <div key={s} className="flex items-start gap-3 p-4 rounded-lg border border-[#E5E7EB] bg-white">
              <Users className="w-4 h-4 text-[#1E3A8A] mt-0.5 flex-shrink-0" />
              <span className="text-[#0F1E47] text-sm font-medium">{s}</span>
            </div>
          ))}
        </div>
      </Section>

      {/* Professional Philosophy */}
      <Section title="Professional Philosophy" accent="#C8A157" warm testId="founder-philosophy">
        <div className="rounded-2xl bg-[#0F1E47] text-white p-8 sm:p-10 relative overflow-hidden">
          <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-[#C8A157]/12 blur-3xl" />
          <div className="relative">
            <Lightbulb className="w-7 h-7 text-[#C8A157] mb-4" />
            <blockquote className="zodiac-heading text-xl sm:text-2xl italic leading-[1.5] mb-4">
              "Behind every organizational result lies a hidden architecture of trust, leadership,
              capability, and alignment. Those who learn to <span className="not-italic text-[#C8A157] font-semibold">measure it</span> will shape the future of work."
            </blockquote>
            <p className="text-white/70 text-sm leading-relaxed mt-4">
              The Institute's work flows from this principle: that workforce intelligence is
              not an aspiration but a measurable institutional discipline.
            </p>
          </div>
        </div>
      </Section>

      {/* Contact CTA */}
      <section className="bg-white py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Award className="w-10 h-10 text-[#C8A157] mx-auto mb-5" />
          <h2 className="zodiac-heading text-3xl sm:text-4xl font-bold text-[#0F1E47] mb-4 leading-tight">
            Engage with the <span className="italic text-[#991B1B] font-normal">Founder.</span>
          </h2>
          <p className="text-[#475569] mb-8 leading-relaxed">
            For speaking engagements, institutional partnerships, executive consulting,
            or media inquiries — reach out to the Office of the President.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/contact">
              <button className="h-12 px-6 rounded-md bg-[#1E3A8A] text-white hover:bg-[#15307A] font-semibold inline-flex items-center gap-2">
                <Mail className="w-4 h-4" /> Contact the Office
              </button>
            </Link>
            <Link to="/press">
              <button className="h-12 px-6 rounded-md border border-[#0F1E47]/15 text-[#0F1E47] hover:bg-[#FBFAF7] inline-flex items-center gap-2">
                Press & Media
              </button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function Section({ title, accent, warm, testId, children }) {
  return (
    <section
      className={`${warm ? "bg-[#FBFAF7] border-y border-[#E5E7EB]" : "bg-white"} py-16`}
      data-testid={testId}
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="zodiac-eyebrow mb-3 flex items-center gap-3" style={{ color: accent }}>
          <span className="w-8 h-px" style={{ background: accent }} />
          {title}
        </div>
        <h2 className="zodiac-heading text-2xl sm:text-3xl font-bold text-[#0F1E47] mb-8">
          {title}
        </h2>
        {children}
      </div>
    </section>
  );
}
